import { defineComponent, ref, watch, onMounted, h } from 'vue';
import { Line } from 'vue-chartjs';
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Filler
} from 'chart.js';

// Register necessary components from Chart.js
ChartJS.register(Title, Tooltip, Legend, LineElement, CategoryScale, LinearScale, PointElement, Filler);

export default defineComponent({
  name: 'CustomAreaChart',
  components: {
    Line
  },
  props: {
    chartData: {
      type: Object,
      required: true
    },
    chartInterval: {
      type: String,
      default: 'Daily'
    },
    chartUnits: {
      type: String,
      default: ''
    },
    secondaryChartUnits: {
      type: String,
      default: ''
    },
    timeLabels: {
      type: Array,
      default: () => ['1', '2', '3']
    }
  },
  setup(props) {
    const options = ref({
      plugins: {
        legend: {
          position: 'top',
          padding: 25,
          labels: {
            font: {}
          }
        },
        datalabels: {
          display: false
        },
        tooltip: {
          enabled: true,
          intersect: false,
          callbacks: {
            title: (tooltipItems) => {
              if (props.chartInterval === 'Weekly') {
                const index = tooltipItems[0].dataIndex;
                return props.timeLabels[index];
              } else {
                const dateStr = tooltipItems[0].label;
                return formatDate(dateStr);
              }
            },
            label: (tooltipItem) => {
              const formattedValue = new Intl.NumberFormat('en-US', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 4
              }).format(tooltipItem.raw);
              return `${tooltipItem.dataset.label}: ${formattedValue}`;
            }
          }
        }
      },
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        y: {
          stacked: true,
          min: props.chartData.ymin !== null ? parseInt(props.chartData.ymin) : undefined,
          max: props.chartData.ymax !== null ? parseInt(props.chartData.ymax) : undefined,
          position: 'left',
          grid: {
            display: true,
            color: '#e4e6e8'
          },
          ticks: {
            display: true,
            font: {
              size: 10
            },
            callback: (value) => {
              const formattedValue = new Intl.NumberFormat('en-US').format(value);
              return `${formattedValue}`;
            }
          },
          title: {
            display: true,
            text: props.chartUnits || '',
            font: {
              size: 18,
              weight: '600'
            },
            padding: { top: 20, bottom: 0 },
            color: '#64676c'
          }
        },
        x: {
          stacked: true,
          grid: {
            display: false
          },
          ticks: {
            maxTicksLimit: 8,
            font: {
              size: 10
            },
            maxRotation: 0,
            minRotation: 0,
            callback: (value, index) => {
              if (props.chartInterval === 'Weekly') {
                const dateStr = props.chartData.time_labels[index];
                return dateStr;
              } else {
                const dateStr = props.chartData.labels[index];
                return formatDate(dateStr);
              }
            }
          }
        }
      }
    });

    const formatDate = (dateStr) => {
      const date = new Date(dateStr);


      if (props.chartInterval === 'Yearly') {
        return date.getUTCFullYear().toString();
      }

      const getOrdinalSuffix = (day) => {
        const suffixes = ['th', 'st', 'nd', 'rd'];
        const v = day % 100;
        return day + (suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0]);
      };

      const options =
        props.chartInterval === 'Hourly'
          ? {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              hour12: false,
              timeZone: 'UTC',
            }
          : {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
              timeZone: 'UTC',
            };

      const formatted = date.toLocaleString('en-GB', options);

      if (props.chartInterval === 'Hourly') {
        return formatted; // already includes hour
      } else {
        const dayWithOrdinal = getOrdinalSuffix(date.getUTCDate());
        return formatted.replace(date.getUTCDate(), dayWithOrdinal);
      }
    };

    watch(
      () => [props.chartData?.ymin, props.chartData?.ymax],
      ([newYMin, newYMax]) => {
        options.value.scales.y.min = newYMin !== null ? parseInt(newYMin) : undefined;
        options.value.scales.y.max = newYMax !== null ? parseInt(newYMax) : undefined;
      },
      { deep: true }
    );

    onMounted(() => {
      if (props.chartData && props.chartData.datasets) {
        props.chartData.datasets.forEach((dataset) => {
          dataset.fill = true; // Enable fill for area chart
          dataset.pointRadius = props.chartInterval === 'Hourly' ? 1 : 2;
          dataset.pointHoverRadius = 5;
          dataset.pointBackgroundColor = '#7a8185';
          dataset.pointBorderColor = '#7a8185';
        });
      }
    });

    return {
      options
    };
  },
  render() {
    return h('div', { style: { height: '500px' } }, [
      h(Line, {
        key: `${this.chartData.ymin}-${this.chartData.ymax}`, // Unique key to force re-render
        data: this.chartData,
        options: this.options
      })
    ]);
  }
});
